/**
 * Wide-polyline (DXF group 40 / 41 / 43) RIBBON OUTLINE — the pure math the
 * line batcher turns into gl.LINES vertices.
 *
 * # What AutoCAD draws
 *
 * A 2D POLYLINE / LWPOLYLINE vertex carries a **starting width** (group 40)
 * and an **ending width** (group 41) that belong to the SEGMENT STARTING AT
 * THAT VERTEX (Autodesk DXF reference, `AcDbPolyline` / `VERTEX`; ezdxf
 * `LWPolyline` `"xyseb"` vertex format, where `s`/`e` are that segment's start
 * and end width). A heavy POLYLINE's vertex inherits the ENTITY-level
 * `defaultStartWidth` / `defaultEndWidth` (groups 40/41 on the POLYLINE
 * record) when it carries none of its own; an LWPOLYLINE instead picks ONE
 * channel per entity — its group-43 constant width, unless any vertex declares
 * a non-zero 40/41, in which case 43 is "not used" and every span reads 40/41
 * (absent ⇒ 0). Resolving that is `drawing-model`'s `polylineWidth.ts` — the
 * ONE place the rule is written; this module is handed already-resolved
 * per-segment widths and never sees either channel.
 *
 * So a wide polyline is NOT a thick line: each segment is its own trapezoid
 * (a `TRACE`-shaped band, tapering when start ≠ end), the band is CENTRED on
 * the segment, adjacent bands are MITRED at the shared vertex, and the two
 * ends are cut SQUARE (AutoCAD never round-caps a polyline band). ezdxf
 * models exactly this in `ezdxf.render.trace.LinearTrace`, which likewise
 * emits one quadrilateral per station pair and intersects the adjacent
 * offset edges to place the shared corners.
 *
 * # Why an OUTLINE
 *
 * `batchedLayer.ts` is a `gl.LINES` batcher — it has no triangle topology, so
 * the band is delivered as its SILHOUETTE: the two offset edges plus the two
 * square end caps, which is the band's exact boundary. The internal segment
 * divisions are NOT drawn: they are interior to the filled band AutoCAD shows
 * with the default `FILLMODE` = 1, and drawing them would put lines on the
 * canvas the drawing does not contain.
 *
 * RESIDUAL — the band's INTERIOR is not filled, so a wide polyline reads as an
 * outlined ribbon rather than the solid one `FILLMODE` = 1 paints (this IS
 * what AutoCAD shows at `FILLMODE` = 0). The silhouette, and therefore the
 * entity's extent, shape, taper and joints, are exact either way; filling it
 * needs a triangle path this batcher does not have, and would consume these
 * same loops as its outline. Nothing here is a placeholder for that: the
 * boundary is real geometry AutoCAD draws, not a stand-in for the fill.
 *
 * # Composition with bulges
 *
 * Nothing here knows about arcs. The caller tessellates a bulged span into
 * sub-segments FIRST (`./polylineWidthBand.ts::polylineSpanSubSegments`, the
 * one span walk the canvas batcher and the vector plot both call) and
 * interpolates the span's taper across them, so a span that is both bulged
 * AND tapered arrives as a chain of short tapering straight bands whose
 * mitred joints follow the arc — the offset of an arc by a constant width is
 * a concentric arc, and this reproduces it to the same chord tolerance the
 * centreline is tessellated at. That is the same division of labour the bulge
 * work itself used: the adapter emits parametric geometry, the batcher
 * tessellates, and the pure math lives in one module both can call.
 */

import { type Vec3 } from "@dxf-ai-editor/drawing-model";

import { unitVec2, type Vec2 } from "./common";

/**
 * One straight band station pair: the segment `a`→`b` with its own start and
 * end WIDTH (full width, not half — the band reaches ±w/2 from the centre).
 */
export interface WidthSegment {
  a: Vec3;
  b: Vec3;
  /** Full width at `a` (DXF group 40 of the vertex this segment starts at). */
  wa: number;
  /** Full width at `b` (DXF group 41 of that same vertex). */
  wb: number;
}

/**
 * Miter length cap, as a multiple of the joint's larger HALF width. A joint
 * that doubles back on itself has an offset intersection at infinity; past
 * this ratio the joint is BEVELLED (both butt corners emitted, joined by a
 * short edge) instead of growing a spike the drawing does not contain. 4
 * keeps every joint sharper than ~29° mitred, which covers every joint a
 * polyline drawn with PLINE realistically has.
 */
export const MITER_LIMIT = 4;

/** Below this the two segments are treated as collinear (no intersection). */
const PARALLEL_EPS = 1e-12;

/** Below this two offset corners are the same point. */
export const COINCIDENT_EPS = 1e-9;

function cross2(u: Vec2, v: Vec2): number {
  return u.x * v.y - u.y * v.x;
}

/** A segment's unit direction, or `null` when it is degenerate (a == b). */
function segmentDir(seg: WidthSegment): Vec2 | null {
  const dx = seg.b.x - seg.a.x;
  const dy = seg.b.y - seg.a.y;
  if (Math.hypot(dx, dy) < COINCIDENT_EPS) return null;
  return unitVec2(dx, dy);
}

/**
 * Drop degenerate (zero-length in XY) segments — they have no direction, so
 * they have no band, and keeping them would make every joint through them
 * undefined. A polyline of only degenerate segments yields an empty list and
 * the caller falls back to the centreline.
 */
export function dropDegenerateSegments(
  segments: readonly WidthSegment[],
): WidthSegment[] {
  return segments.filter((s) => segmentDir(s) !== null);
}

/** `true` when every segment is zero-width — the band IS the centreline. */
export function allWidthsZero(segments: readonly WidthSegment[]): boolean {
  return segments.every((s) => s.wa === 0 && s.wb === 0);
}

/** The band corner at `p` offset by `half` along `side * normal(dir)`. */
function offsetPoint(p: Vec3, dir: Vec2, half: number, side: number): Vec3 {
  // Left normal of (dx, dy) is (-dy, dx); `side` flips it for the right edge.
  return {
    x: p.x + side * -dir.y * half,
    y: p.y + side * dir.x * half,
    z: p.z,
  };
}

/**
 * The joint between the END of `prev` and the START of `next` on one side of
 * the band: the intersection of the two offset edges (the miter point), or
 * `null` when there is none — the edges are parallel with a width STEP
 * between them (a genuine discontinuity AutoCAD shows as a step, because the
 * two segments declare different widths at the shared vertex), or the miter
 * would exceed {@link MITER_LIMIT}. `null` makes the caller emit both butt
 * corners, which is the bevelled/stepped joint.
 */
function miterJoint(
  prevEnd: Vec3,
  prevDir: Vec2,
  nextStart: Vec3,
  nextDir: Vec2,
  limit: number,
): Vec3 | null {
  const denom = cross2(prevDir, nextDir);
  const rx = nextStart.x - prevEnd.x;
  const ry = nextStart.y - prevEnd.y;
  if (Math.abs(denom) < PARALLEL_EPS) {
    // Collinear: either the same corner (continuous width — the common case,
    // including every interior joint of a uniform-width polyline) or a step.
    return Math.hypot(rx, ry) < COINCIDENT_EPS ? prevEnd : null;
  }
  const t = (rx * nextDir.y - ry * nextDir.x) / denom;
  if (!Number.isFinite(t) || Math.abs(t) > limit) return null;
  return {
    x: prevEnd.x + prevDir.x * t,
    y: prevEnd.y + prevDir.y * t,
    // The joint sits AT the shared vertex in plan; its elevation is that
    // vertex's, never an extrapolation along the miter.
    z: prevEnd.z,
  };
}

/**
 * One side (`+1` left / `-1` right) of the band, as a point run. `ring` is
 * `true` only for a genuine closed RING (a closed polyline with more than one
 * live segment): a single segment has no wrap joint, so it is walked open.
 */
function offsetSide(
  segments: readonly WidthSegment[],
  ring: boolean,
  side: number,
): Vec3[] {
  const n = segments.length;
  const dirs: Vec2[] = segments.map((s) => segmentDir(s)!);
  const starts: Vec3[] = segments.map((s, i) =>
    offsetPoint(s.a, dirs[i]!, s.wa / 2, side),
  );
  const ends: Vec3[] = segments.map((s, i) =>
    offsetPoint(s.b, dirs[i]!, s.wb / 2, side),
  );

  const out: Vec3[] = [];
  const pushJoint = (prev: number, next: number): void => {
    const limit =
      MITER_LIMIT *
      Math.max(
        Math.abs(segments[prev]!.wb) / 2,
        Math.abs(segments[next]!.wa) / 2,
        COINCIDENT_EPS,
      );
    const j = miterJoint(
      ends[prev]!,
      dirs[prev]!,
      starts[next]!,
      dirs[next]!,
      limit,
    );
    if (j) {
      out.push(j);
      return;
    }
    // Bevel / width step: both butt corners, joined by the edge between them.
    out.push(ends[prev]!, starts[next]!);
  };

  for (let i = 0; i < n; i += 1) {
    if (i === 0) {
      if (ring) pushJoint(n - 1, 0);
      else out.push(starts[0]!);
      continue;
    }
    pushJoint(i - 1, i);
  }
  if (!ring) out.push(ends[n - 1]!);
  return out;
}

/**
 * The band's silhouette as CLOSED point loops, ready to be stroked.
 *
 * An OPEN polyline yields ONE loop — the left edge forward, then the right
 * edge backward. Closing that loop draws the two square END CAPS for free,
 * which is exactly AutoCAD's flat polyline end.
 *
 * A CLOSED polyline yields TWO loops, the outer and the inner edge: a closed
 * band is an annulus and has no caps, so joining its two edges would draw a
 * cut across the ribbon that is not in the drawing.
 *
 * Returns `[]` when there is nothing to outline (fewer than one non-degenerate
 * segment); the caller then strokes the centreline instead.
 */
export function widePolylineOutlineLoops(
  segments: readonly WidthSegment[],
  closed: boolean,
): Vec3[][] {
  const live = dropDegenerateSegments(segments);
  if (live.length === 0) return [];
  const ring = closed && live.length > 1;
  const left = offsetSide(live, ring, +1);
  const right = offsetSide(live, ring, -1);
  if (ring) return [left, right];
  return [[...left, ...right.slice().reverse()]];
}
